import { Badge, Box, Button, Checkbox, Divider, Flex, Grid, Input, LoadingOverlay, NumberInput, Text, Textarea } from "@mantine/core";
import { IconCurrencyTugrik } from "@tabler/icons";
import React from "react";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { LbfRequestApi } from "../../apis";
import { IAuth } from "../../interfaces/IAuth";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReference } from "../../models/General";
import { LbfRequest } from "../../models/LbfRequest";
import { numberRegex, tugrug } from "../../utils";
import { dateSecFormat } from "../../utils/date";
import HttpHandler from "../../utils/http-handler";
import { Message } from "../../utils/message";
import { Form } from "../form";
import { CheckboxField } from "../form/check-box";
import { NumberCurrencyField } from "../form/number-currency-field";
import { RadioGroupField } from "../form/radio-group-field";
import { TextareaField } from "../form/textarea-field";

const schema = yup.object({
  approve: yup.string().required("Заавал бөглөнө!").nullable(),
  approvedAmount: yup.string().when("approve", {
    is: "SUCCESS",
    then: yup
      .string()
      .min(1, "Хэтэрхий бага утга байна!")
      .max(16, "Хэтэрхий их утга байна!")
      .matches(numberRegex, "Тоо оруулна уу!")
      .required("Заавал бөглөнө үү!")
      .nullable(),
  }),
});

type IFormData = {
  approve: string;
  finance_board: boolean;
  respondDesc: string;
  approvedAmount: string;
};

const clientStaffLoginTypes = [
  {
    name: "Санхүүжилт зөвшөөрөх",
    code: "SUCCESS",
  },
  {
    name: "Санхүүжилтийг татгалзах",
    code: "FAILED",
  },
];

export function FundingRequestBuyerRequest({
  data,
  onNext,
  onBack,
  onSuccess,
  onCancel,
  reload,
}: {
  data: LbfRequest;
  onCancel: () => void;
  onNext: () => void;
  onBack: () => void;
  onSuccess: () => void;
  reload: () => void;
}) {
  const { requestStatus } = useSelector((state: { general: IGeneral }) => state.general);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [initial] = React.useState<IFormData>({
    approvedAmount: "",
    approve: "",
    respondDesc: "",
    finance_board: false,
  });
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);

  const onSubmit = async (value: IFormData) => {
    setLoading(true);
    try {
      if (value.approve === "SUCCESS") {
        await LbfRequestApi.respond(data.id, {
          approve: true,
          approvedAmount: value.approvedAmount,
          respondDesc: value.respondDesc,
        });
        onNext();
        Message.success("Амжилттай санхүүжилтийн хүсэлтийг зөвшөөрсөн!");
      } else {
        await LbfRequestApi.respond(data.id, {
          approve: false,
          respondDesc: value.respondDesc,
        });
        Message.success("Амжилттай санхүүжилтийн хүсэлтийг татгалзсан!");
        onCancel();
      }

      onSuccess();
      reload();
    } catch (error) {
      Message.error((error as HttpHandler)?.message!);
    }
    setLoading(false);
  };

  return (
    <Form validationSchema={schema} onSubmit={onSubmit} initialValues={initial}>
      {({ values }) => {
        return (
          <Flex sx={{ position: "relative" }} direction="column">
            <LoadingOverlay visible={loading} />
            <Grid mt="xs">
              <Grid.Col span={12}>
                <Divider color="gray" styles={{ label: { fontSize: 16, fontWeight: 500 } }} label="ХҮСЭЛТИЙН ЕРӨНХИЙ МЭДЭЭЛЭЛ" />
              </Grid.Col>

              <Grid.Col span={4}>
                <Input.Wrapper label="Код, огноо цаг: ">
                  <Flex justify="flex-start" align="flex-start" direction="row" wrap="wrap" gap={5}>
                    <Text size="sm" weight={500} c="#3e166e">
                      {data?.refCode || "-"},
                    </Text>
                    <Text color="indigo" fz="sm" fw={600}>
                      {dateSecFormat(data?.createdAt)}
                    </Text>
                  </Flex>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Хүсэлт гаргасан ажилтан: ">
                  <Flex justify="flex-start" align="flex-start" direction="row" wrap="wrap" gap={5}>
                    <Text size="sm" weight={500} c="#3e166e">
                      {data?.requestedUser?.lastName ? data?.requestedUser?.lastName[0] : ""}
                      {data?.requestedUser?.lastName && "."}
                      {data?.requestedUser?.firstName || "-"}
                    </Text>
                    <Badge size="md" radius="sm" variant="outline" mt={2} color="indigo">
                      {data?.requestedUser?.email || "-"}
                    </Badge>
                  </Flex>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Хүсэлтийн төлөв: ">
                  <br />
                  <Badge
                    size="md"
                    radius="sm"
                    variant="outline"
                    style={{
                      border: `${(requestStatus || []).find((c: any) => c?.code === data?.lbfRequestStatus)?.color} 1px solid`,
                      color: `${(requestStatus || []).find((c: any) => c?.code === data?.lbfRequestStatus)?.color}`,
                    }}>
                    {(requestStatus || []).find((c: IReference) => c.code === data?.lbfRequestStatus)?.name}
                  </Badge>
                </Input.Wrapper>
              </Grid.Col>

              <Grid.Col span={12}>
                <Divider color="gray" styles={{ label: { fontSize: 16, fontWeight: 500 } }} label="ХАРИЛЦАГЧИЙН МЭДЭЭЛЭЛ" />
              </Grid.Col>

              <Grid.Col span={4}>
                <Input.Wrapper label="Харилцагчийн код:">
                  <Text color="indigo" sx={{ fontFamily: "Greycliff CF, sans-serif" }} fz="sm" fw={600}>
                    {data?.requestedClientCif || "-"}
                  </Text>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Харилцагчийн нэр:">
                  <Flex justify="flex-start" align="flex-start" direction="row" wrap="wrap" gap={5}>
                    <Text size="sm" weight={500} c="#3e166e">
                      {data?.requestedBusiness?.partner?.refCode || "-"},
                    </Text>
                    <Text color="indigo" fz="sm" fw={600}>
                      {data?.requestedBusiness?.partner?.businessName || "-"}
                    </Text>
                  </Flex>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Татвар төлөгчийн дугаар:">
                  <Box color="indigo" mt={4}>
                    <Text color="indigo" sx={{ fontFamily: "Greycliff CF, sans-serif" }} fz="sm" fw={600}>
                      {data?.requestedBusiness?.partner?.regNumber || "-"}
                    </Text>
                  </Box>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Хүсэлт хамаарах бизнес:">
                  <Flex justify="flex-start" align="flex-start" direction="row" wrap="wrap" gap={5}>
                    <Text size="sm" weight={500} c="#3e166e">
                      {data?.requestedBusiness?.refCode || "-"},
                    </Text>
                    <Text color="indigo" fz="sm" fw={600}>
                      {data?.requestedBusiness?.profileName || "-"},
                    </Text>
                  </Flex>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Хөтөлбөрийн нэр:">
                  <Flex justify="flex-start" align="flex-start" direction="row" wrap="wrap" gap={5}>
                    <Text size="sm" weight={500} c="#3e166e">
                      {data?.lbfProgram?.refCode || "-"},
                    </Text>
                    <Text color="indigo" fz="sm" fw={600}>
                      {data?.lbfProgram?.name || "-"}
                    </Text>
                  </Flex>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Бүтээгдэхүүнийн нэр:">
                  <Flex justify="flex-start" align="flex-start" direction="row" wrap="wrap" gap={5}>
                    <Text size="sm" weight={500} c="#3e166e">
                      {data?.lbfProduct?.refCode || "-"},
                    </Text>
                    <Text color="indigo" fz="sm" fw={600}>
                      {data?.lbfProduct?.name || "-"}
                    </Text>
                  </Flex>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Худалдан авагч анкор эсэх:">
                  <Box color="indigo" mt={4}>
                    <Text color="indigo" sx={{ fontFamily: "Greycliff CF, sans-serif" }} fz="sm" fw={600}>
                      {data?.lbfProductType === "SUPPLIER_LED" && data.type === "SUPPLIER" ? "Тийм" : "Үгүй"}
                    </Text>
                  </Box>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}></Grid.Col>
              <Grid.Col span={4}></Grid.Col>

              <Grid.Col span={12}>
                <Divider color="gray" styles={{ label: { fontSize: 16, fontWeight: 500 } }} label="ХҮСЭЛТ ШАЛГУУР ХАНГАХ БАЙДАЛ" />
              </Grid.Col>

              <Grid.Col span={4}>
                <Input.Wrapper label="Санхүүжилт хүссэн дүн:">
                  <Box color="indigo" mt={4}>
                    <Text color="indigo" sx={{ fontFamily: "Greycliff CF, sans-serif" }} fz="sm" fw={600}>
                      {tugrug(data?.requestedAmount || 0)}
                    </Text>
                  </Box>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Нэхэмжлэх төлөх огноо:">
                  <Box color="indigo" mt={4}>
                    <Text color="indigo" sx={{ fontFamily: "Greycliff CF, sans-serif" }} fz="sm" fw={600}>
                      {dateSecFormat(data?.invoice?.paymentDate)}
                    </Text>
                  </Box>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Нэхэмжлэх үлдэгдэл:">
                  <Text color="indigo" fz="sm" fw={600}>
                    {tugrug(data?.invoice?.amountToPay || 0)}
                  </Text>
                </Input.Wrapper>
              </Grid.Col>

              <Grid.Col span={4}>
                <Input.Wrapper label="Олголт хүлээн авах данс:">
                  <Flex justify="flex-start" align="flex-start" direction="row" wrap="wrap" gap={5}>
                    <Text size="sm" weight={500} c="#3e166e">
                      {data?.type === "SUPPLIER" ? <>{data?.invoice?.supplierAccount?.number || "-"}</> : <>{data?.invoice?.buyerAccount?.number}</>},
                    </Text>
                    <Text color="indigo" fz="sm" fw={600}>
                      {data?.type === "SUPPLIER" ? (
                        <>
                          {data?.invoice?.supplierAccount?.name || "-"}, {data?.invoice?.supplierAccount?.bankName || "-"}
                        </>
                      ) : (
                        <>
                          {data?.invoice?.buyerAccount?.name}, {data?.invoice?.buyerAccount?.bankName || "-"}
                        </>
                      )}
                    </Text>
                  </Flex>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}></Grid.Col>
              <Grid.Col span={4}></Grid.Col>

              <Grid.Col span={12}>
                <Divider color="gray" styles={{ label: { fontSize: 16, fontWeight: 500 } }} label="ШИЙДВЭРИЙН МЭДЭЭЛЭЛ" />
              </Grid.Col>
              {data?.lbfRequestStatus === "REQUESTED" ? (
                <>
                  <Grid.Col span={12}>
                    <CheckboxField name="finance_board" label="">
                      {values.finance_board ? "Санхүүжилтийн хүсэлтийн мэдэээллийг хянасан" : "Санхүүжилтийн хүсэлтийн мэдэээллийг хянасан"}
                    </CheckboxField>
                  </Grid.Col>

                  <Grid.Col span={12}>
                    <RadioGroupField
                      label=""
                      withAsterisk={false}
                      name="approve"
                      options={(clientStaffLoginTypes || []).map((c) => ({
                        label: c.name,
                        value: c.code,
                      }))}
                    />
                  </Grid.Col>

                  {values.approve === "SUCCESS" && (
                    <Grid.Col span={4}>
                      <NumberCurrencyField
                        max={data?.requestedAmount >= data?.invoice?.amountToPay ? data?.invoice?.amountToPay : data?.requestedAmount}
                        name="approvedAmount"
                        label="Санхүүжилт олгох дүн:"
                        placeholder="Мөнгөн дүн"
                        required
                      />
                      <Text c="dimmed" fz="xs" fw={500} style={{ color: "#FA5252" }}>
                        {`Санхүүжилтийн хүсэлтийн дүн ${tugrug(data?.requestedAmount || 0)}, нэхэмжлэхийн үлдэгдэл ${tugrug(
                          data?.invoice?.amountToPay || 0,
                        )}. Та эдгээр дүнгээс өндөр дүн оруулах боломжгүй! `}
                      </Text>
                    </Grid.Col>
                  )}
                  <Grid.Col span={8}>
                    <TextareaField name="respondDesc" label="Тайлбар:" placeholder="Тайлбар энд бичих" withAsterisk={false} maxLength={255} />
                  </Grid.Col>
                </>
              ) : (
                <>
                  <Grid.Col span={12}>
                    <Checkbox
                      checked={true}
                      onChange={() => {}}
                      label={
                        <Text color="gray" fz="sm" fw={500}>
                          Санхүүжилтийн хүсэлтийн мэдэээллийг хянасан
                        </Text>
                      }
                    />
                  </Grid.Col>

                  {data.isApproved && (
                    <Grid.Col span={4}>
                      <NumberInput
                        label="Санхүүжилт олгох дүн: "
                        value={data.approvedAmount}
                        hideControls
                        parser={(value: any) => value.replace(/\$\s?|(,*)/g, "")}
                        formatter={(value: any) => (!Number.isNaN(parseFloat(value)) ? ` ${value}`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",") : " ")}
                        rightSection={<IconCurrencyTugrik style={{ width: 20, height: 20 }} stroke={0.8} />}
                        disabled
                      />
                    </Grid.Col>
                  )}
                  <Grid.Col span={8}>
                    <Textarea label="Тайлбар: " value={data?.respondDesc} disabled />
                  </Grid.Col>
                </>
              )}
              <Grid.Col span={12}>
                <Divider color="gray" styles={{ label: { fontSize: 16, fontWeight: 500 } }} />
              </Grid.Col>
            </Grid>
            <Flex direction="row" justify="space-between" mt={10}>
              <Button
                variant="default"
                onClick={() => {
                  onBack();
                }}>
                Буцах
              </Button>
              <Flex gap="md">
                {(user?.permissions || []).filter((c) => c.module === "LBF" && c.code === "LBF_REQUEST_RESPOND_BUYER").length > 0 && (
                  <>
                    <Button
                      variant="filled"
                      color="red"
                      disabled={!values.finance_board || values.approve === "SUCCESS" || data?.lbfRequestStatus !== "REQUESTED"}
                      type="submit">
                      Татгалзах
                    </Button>
                    <Button
                      variant="filled"
                      disabled={!values.finance_board || values.approve === "FAILED" || data?.lbfRequestStatus !== "REQUESTED"}
                      type="submit">
                      Зөвшөөрөх
                    </Button>
                  </>
                )}
              </Flex>
            </Flex>
          </Flex>
        );
      }}
    </Form>
  );
}
